import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import jsPDF from 'jspdf';

const AdminPollResults = () => {
    const [activePoll, setActivePoll] = useState(null);
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchActivePoll();
        const interval = setInterval(fetchActivePoll, 3000);
        return () => clearInterval(interval);
    }, []);

    const fetchActivePoll = async () => {
        try {
            const res = await api.get('/polls/active');
            setActivePoll(res.data);
            if (res.data) {
                const statRes = await api.get(`/polls/${res.data.id}/stats`);
                setStats(statRes.data);
            } else {
                setStats(null);
            }
        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportPDF = () => {
        if (!activePoll || !stats) return;

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 14;

        // Banner Header
        doc.setFillColor(0, 89, 91); // Theme primary #00595b
        doc.rect(0, 0, pageWidth, 26, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text("RESULTADOS DE PREGUNTA INTERACTIVA", margin, 12);

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        const dateStr = new Date().toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' });
        doc.text(`Emisión: ${dateStr}`, pageWidth - margin, 12, { align: 'right' });
        doc.text(`UN HITO en la Prevención frente al VRS — Pfizer`, margin, 19);

        let startY = 36;

        // Question Title Box
        doc.setFillColor(241, 245, 249);
        doc.rect(margin, startY, pageWidth - (margin * 2), 14, 'F');
        doc.setTextColor(15, 23, 42);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(`Pregunta: ${activePoll.question}`, margin + 4, startY + 9);

        startY += 20;

        // Overview Summary Box
        const pctParticip = stats.totalUsers > 0 ? Math.round((stats.totalVotes / stats.totalUsers) * 100) : 0;
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text(`Total Usuarios Registrados: ${stats.totalUsers || 0}   |   Votos Recibidos: ${stats.totalVotes || 0}   |   Participación: ${pctParticip}%`, margin, startY);

        startY += 8;

        // Table Header
        doc.setFillColor(226, 232, 240);
        doc.rect(margin, startY, pageWidth - (margin * 2), 8, 'F');
        doc.setTextColor(15, 23, 42);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text("Opción de Respuesta", margin + 3, startY + 5.5);
        doc.text("Votos", pageWidth - margin - 45, startY + 5.5, { align: 'right' });
        doc.text("Porcentaje", pageWidth - margin - 6, startY + 5.5, { align: 'right' });

        startY += 10;

        // Options List
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);

        (stats.results || []).forEach((res, idx) => {
            const percent = stats.totalVotes > 0 ? Math.round((res.votes / stats.totalVotes) * 100) : 0;

            if (idx % 2 === 0) {
                doc.setFillColor(248, 250, 252);
                doc.rect(margin, startY - 4, pageWidth - (margin * 2), 8, 'F');
            }

            doc.setTextColor(51, 65, 85);
            doc.text(`${idx + 1}. ${res.option_text || ''}`, margin + 3, startY + 1.5);
            doc.text(`${res.votes || 0} votos`, pageWidth - margin - 45, startY + 1.5, { align: 'right' });
            doc.text(`${percent}%`, pageWidth - margin - 6, startY + 1.5, { align: 'right' });

            doc.setDrawColor(226, 232, 240);
            doc.line(margin, startY + 4, pageWidth - margin, startY + 4);
            startY += 8;
        });

        // Page Numbering Footer
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(`Página 1 de 1`, pageWidth - margin, pageHeight - 8, { align: 'right' });
        doc.text(`Resultados interactivos exportados en texto vectorial seleccionable`, margin, pageHeight - 8);

        doc.save(`Resultados_Trivia_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    if (isLoading) return (
        <div className="flex items-center justify-center h-[60vh]">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
    );

    if (!activePoll) return (
        <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 p-20 text-center animate-fadeIn">
            <div className="text-6xl mb-6 opacity-20">📊</div>
            <h3 className="text-xl font-bold text-slate-800 uppercase tracking-tighter">Sin Votaciones Activas</h3>
            <p className="text-slate-400 mt-2 text-sm font-medium">Los resultados aparecerán aquí cuando el Staff publique una pregunta.</p>
        </div>
    );

    return (
        <div className="animate-fadeIn space-y-8">
            <div className="bg-white rounded-[40px] shadow-2xl border border-slate-100 p-12 overflow-hidden relative">
                <div className="flex justify-between items-start mb-12">
                    <div>
                        <span className="bg-blue-600 text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-[0.2em] shadow-lg shadow-blue-500/30">Votación en Tiempo Real</span>
                        <h1 className="text-4xl font-black text-slate-900 mt-6 tracking-tight leading-tight max-w-4xl">
                            {activePoll.question}
                        </h1>
                    </div>
                    <div className="flex gap-4 items-center">
                        <button
                            onClick={handleExportPDF}
                            className="bg-[#00595b] hover:bg-[#00484c] text-white px-5 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider shadow-md transition-all flex items-center"
                        >
                            <span className="mr-2 text-base">📄</span> Exportar Resultados (PDF Texto)
                        </button>
                        <div className="text-right">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Participación</p>
                            <p className="text-3xl font-black text-blue-600">
                                {stats?.totalUsers > 0 ? Math.round((stats.totalVotes / stats.totalUsers) * 100) : 0}%
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-6 mb-12">
                    <div className="bg-slate-50 p-8 rounded-[30px] border border-slate-100">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Usuarios</p>
                        <p className="text-4xl font-black text-slate-900">{stats?.totalUsers || 0}</p>
                    </div>
                    <div className="bg-slate-50 p-8 rounded-[30px] border border-slate-100">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Votos Recibidos</p>
                        <div className="flex items-baseline gap-2">
                            <p className="text-4xl font-black text-emerald-500">{stats?.totalVotes || 0}</p>
                            <span className="text-slate-300 font-bold">Votos</span>
                        </div>
                    </div>
                    <div className="bg-blue-600 p-8 rounded-[30px] text-white shadow-xl shadow-blue-900/20">
                        <p className="text-[10px] font-black text-white/60 uppercase tracking-widest mb-2">Estado</p>
                        <p className="text-4xl font-black uppercase tracking-tighter">Activa</p>
                    </div>
                </div>

                <div className="space-y-10">
                    {stats?.results.map((res, index) => {
                        const percent = stats.totalVotes > 0 ? Math.round((res.votes / stats.totalVotes) * 100) : 0;
                        const colors = ['from-blue-600 to-indigo-600', 'from-purple-600 to-pink-600', 'from-emerald-500 to-teal-500', 'from-orange-500 to-amber-500'];
                        const colorClass = colors[index % colors.length];

                        return (
                            <div key={res.id} className="group">
                                <div className="flex justify-between items-end mb-4">
                                    <div className="flex items-center gap-4">
                                        <span className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-black text-xs">{index + 1}</span>
                                        <span className="text-xl font-bold text-slate-700">{res.option_text}</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="block text-3xl font-black text-slate-900">{percent}%</span>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{res.votes} Votos</span>
                                    </div>
                                </div>
                                <div className="w-full h-8 bg-slate-100 rounded-2xl overflow-hidden p-1 shadow-inner">
                                    <div 
                                        className={`h-full bg-gradient-to-r ${colorClass} rounded-xl transition-all duration-1000 ease-out shadow-lg`}
                                        style={{ width: `${percent}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <p className="text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                Actualización automática cada 3 segundos • Datos sincronizados con base de datos central
            </p>
        </div>
    );
};

export default AdminPollResults;
